import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import Form from "@/components/Form";
import SaveButton from "@/components/SaveButton";
import SystemRoleSelect from "@/components/SystemRoleSelect";
import TaxStatusSelect from "@/components/TaxStatusSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DEFAULT_LANGUAGE } from "@/i18n";
import { isRequired } from "@/lib/form";
import {
  PAYROLL_COMMENT_MAX_LENGTH,
  SYSTEM_ROLE_CODE,
  updateUser,
} from "@/schemas/user";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useUsersContext } from "../context";
import { useUserData, useUserForm } from "../hooks";

const schema = updateUser.innerType().shape;

const UserUpdateForm = memo(() => {
  const { t } = useTranslation();
  const { handleUpdate } = useUsersContext();
  const user = useUserData();

  const defaultValues = useMemo(
    () => ({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone_number: user?.phone_number || "",
      cognito_id: user?.cognito_id || "",
      preferred_language_iso_code: DEFAULT_LANGUAGE,
      system_role_id: user?.system_role_id || "",
      register_as_contractor: user?.contractor_is_active || false,
      tax_status: user?.tax_status || undefined,
      payroll_comment: user?.payroll_comment || undefined,
    }),
    [user],
  );

  const {
    form,
    showRegisterAsContractorCheckbox,
    showTaxStatusSelect,
    showPayrollCommentField,
  } = useUserForm(updateUser, defaultValues);

  return (
    <div>
      <h3 className="text-lg font-medium mb-4">
        {t("users.updateForm.title")}
      </h3>
      <Form form={form} handleSubmit={handleUpdate}>
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="first_name"
            label={t("users.fields.firstName")}
            required={isRequired(schema.first_name)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          <Field
            name="last_name"
            label={t("users.fields.lastName")}
            required={isRequired(schema.last_name)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          <Field
            name="email"
            label={t("users.fields.email")}
            required={isRequired(schema.email)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          <Field
            name="phone_number"
            label={t("users.fields.phoneNumber")}
            required={isRequired(schema.phone_number)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          <Field
            name="system_role_id"
            label={t("users.fields.systemRole")}
            required={isRequired(schema.system_role_id)}
            render={({ field }) => (
              <SystemRoleSelect
                defaultItem={{
                  id: user?.system_role_id,
                  name: user?.system_role_name,
                }}
                {...field}
                loading="eager"
                excludeSystemRoleCodes={SYSTEM_ROLE_CODE.CONTRACTOR}
              />
            )}
          />
          {showRegisterAsContractorCheckbox && (
            <Field
              name="register_as_contractor"
              label={t("users.fields.register_as_contractor")}
              render={({ field }) => (
                <div className="mt-2">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="border-gray-400"
                  />
                </div>
              )}
            />
          )}
          {showTaxStatusSelect && (
            <Field
              name="tax_status"
              label={t("users.fields.taxStatus")}
              required
              render={({ field }) => (
                <TaxStatusSelect
                  defaultItem={{ id: user?.tax_status, name: user?.tax_status }}
                  {...field}
                  loading="eager"
                />
              )}
            />
          )}
          {showPayrollCommentField && (
            <Field
              name="payroll_comment"
              label={t("users.fields.payroll_comment")}
              render={({ field }) => (
                <Textarea
                  className="w-full"
                  {...field}
                  placeholder={t("users.placeholders.payroll_comment")}
                  rows={3}
                  maxLength={PAYROLL_COMMENT_MAX_LENGTH}
                />
              )}
            />
          )}
        </Fieldset>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <SaveButton />
        </div>
      </Form>
    </div>
  );
});

export default UserUpdateForm;
